import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { billingApi, Invoice, BillingAccount } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import TopUpDialog from './TopUpDialog';
import { useTranslation } from 'react-i18next';
import i18n, { currentLocale } from '../i18n';
import '../styles/reports.css';
import '../styles/users.css';

// `value` is the stored type/status — only labelKey is interface text.
const TYPE_OPTIONS = [
  { value: '', labelKey: 'billing.allTypes' },
  { value: 'payment_confirmation', labelKey: 'billing.typeTopup' },
  { value: 'topup_request', labelKey: 'billing.typeTopupPending' },
  { value: 'spend_invoice', labelKey: 'billing.typeSpend' },
];

const STATUS_OPTIONS = [
  { value: '', labelKey: 'billing.allStatuses' },
  { value: 'ongoing', labelKey: 'billing.st_ongoing' },
  { value: 'paid', labelKey: 'billing.st_paid' },
  { value: 'bonus', labelKey: 'billing.st_bonus' },
  { value: 'issued', labelKey: 'billing.st_issued' },
];

function formatDate(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function formatAmount(amount: number): { whole: string; cents: string } {
  const parts = amount.toFixed(2).split('.');
  const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u2009');
  return { whole, cents: parts[1] };
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'paid': return '#27ae60';
    case 'bonus': return '#2e7d32';
    case 'issued': return '#2980b9';
    case 'ongoing': return '#90a4ae';
    default: return '#546e7a';
  }
}

function getStatusLabel(status: string): string {
  return i18n.t(`billing.st_${status}`,
    { defaultValue: status.charAt(0).toUpperCase() + status.slice(1) });
}

const TransactionsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'regional_admin';
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [totalsMeta, setTotalsMeta] = useState<{ currency: string; is_sense_staff: boolean }>({ currency: 'EUR', is_sense_staff: false });
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const [periodMonth, setPeriodMonth] = useState('');
  const [periodYear, setPeriodYear] = useState('');
  const [ownerQuery, setOwnerQuery] = useState('');
  const [selectedOwner, setSelectedOwner] = useState<{ name: string; type: string } | null>(null);
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const ownerRef = useRef<HTMLDivElement>(null);
  // External roles manage a single account, so this page is the only place they
  // can start a top-up — Billing stays admin-only.
  const [ownAccount, setOwnAccount] = useState<BillingAccount | null>(null);
  const [showTopup, setShowTopup] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await billingApi.listInvoices({ limit: 200 });
      setInvoices(data.invoices);
      if (data.totals) setTotalsMeta(data.totals);
    } catch (e) {
      console.error('Failed to load transactions', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  useEffect(() => {
    if (isAdmin) return;
    billingApi.listAccounts()
      .then(d => setOwnAccount(d.accounts?.length === 1 ? d.accounts[0] : null))
      .catch(() => setOwnAccount(null));
  }, [isAdmin]);

  const handleDownloadPdf = async (id: number, invoiceNumber: string, type: string) => {
    try {
      const blob = await billingApi.downloadInvoicePdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'payment_confirmation'
        ? `confirmation_${invoiceNumber}.pdf`
        : `invoice_${invoiceNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(t('billing.errDownloadPdf'));
    }
  };

  const [confirmDelete, setConfirmDelete] = useState<{ id: number; label: string; reverses: boolean } | null>(null);
  const handleDelete = (id: number, invoiceNumber: string, total: number, type?: string, status?: string) => {
    // In-app confirm, not window.confirm — the native dialog gets suppressed by the
    // browser after a few uses in a session, which silently no-ops the delete.
    const reverses = type === 'payment_confirmation' || (type === 'topup_request' && status === 'paid');
    setConfirmDelete({ id, label: `${invoiceNumber} (${total.toFixed(2)})`, reverses });
  };
  const doDelete = async () => {
    if (!confirmDelete) return;
    const { id } = confirmDelete;
    setConfirmDelete(null);
    try {
      await billingApi.deleteInvoice(id);
      loadTransactions();
    } catch (e: any) {
      alert(e.response?.data?.detail || t('billing.errDeleteTransaction'));
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    if (!window.confirm(t('billing.confirmMarkAs', { status: getStatusLabel(newStatus) }))) return;
    try {
      await billingApi.updateInvoiceStatus(id, newStatus);
      loadTransactions();
    } catch (e: any) {
      alert(e.response?.data?.detail || t('billing.errStatusUpdate'));
    }
  };

  const ownerOptions = useMemo(() => {
    const map = new Map<string, { name: string; type: string }>();
    invoices.forEach(inv => {
      const ag = (inv as any).agency_name;
      const fl = (inv as any).freelancer_name;
      if (ag && inv.scope !== 'agency_account') map.set(`agency:${ag}`, { name: ag, type: 'Agency' });
      if (fl) map.set(`freelancer:${fl}`, { name: fl, type: 'Freelancer' });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [invoices]);

  const filteredOwners = ownerQuery
    ? ownerOptions.filter(o => o.name.toLowerCase().includes(ownerQuery.toLowerCase()))
    : ownerOptions;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ownerRef.current && !ownerRef.current.contains(e.target as Node)) {
        setShowOwnerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const officeOptions = useMemo(() => {
    const set = new Set<string>();
    invoices.forEach(inv => { if (inv.office_name) set.add(inv.office_name); });
    return Array.from(set).sort();
  }, [invoices]);

  const hasFilters = typeFilter || statusFilter || search || officeFilter || selectedOwner || periodMonth || periodYear;

  const clearFilters = () => {
    setTypeFilter('');
    setStatusFilter('');
    setSearch('');
    setOfficeFilter('');
    setPeriodMonth('');
    setPeriodYear('');
    setSelectedOwner(null);
    setOwnerQuery('');
  };

  const filtered = invoices.filter(inv => {
    if (typeFilter && inv.type !== typeFilter) return false;
    if (statusFilter && inv.status !== statusFilter) return false;
    if (officeFilter && inv.office_name !== officeFilter) return false;
    if (periodMonth || periodYear) {
      if (!inv.period_start) return false;
      const d = new Date(inv.period_start);
      if (periodMonth && String(d.getMonth() + 1).padStart(2, '0') !== periodMonth) return false;
      if (periodYear && String(d.getFullYear()) !== periodYear) return false;
    }
    if (selectedOwner) {
      const ag = (inv as any).agency_name;
      const fl = (inv as any).freelancer_name;
      if (selectedOwner.type === 'Agency' && ag !== selectedOwner.name) return false;
      if (selectedOwner.type === 'Freelancer' && fl !== selectedOwner.name) return false;
    }
    if (!debouncedSearch) return true;
    const q = debouncedSearch.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(q) ||
      inv.advertiser_name?.toLowerCase().includes(q) ||
      inv.advertiser_id?.toLowerCase().includes(q)
    );
  });

  filtered.sort((a, b) => {
    const pa = a.period_start ? new Date(a.period_start).getTime() : new Date(a.created_at).getTime();
    const pb = b.period_start ? new Date(b.period_start).getTime() : new Date(b.created_at).getTime();
    return pb - pa;
  });

  const totals = useMemo(() => {
    // Aggregate in NATIVE currency first, then convert each currency's NET
    // to the base once. This guarantees same-currency in/out cancels exactly
    // (e.g. UAH spend paid by UAH top-up nets to 0 UAH = 0 EUR), instead of
    // accumulating row-by-row FX rounding loss.
    type Bucket = { topup: number; spend: number; bonus: number; rateToBase?: number };
    const buckets: Record<string, Bucket> = {};

    const getBucket = (ccy: string): Bucket => {
      if (!buckets[ccy]) buckets[ccy] = { topup: 0, spend: 0, bonus: 0 };
      return buckets[ccy];
    };

    filtered.forEach(inv => {
      const b = getBucket(inv.currency);
      // Capture an FX rate for this currency from the first row that exposes total_eur.
      if (b.rateToBase === undefined && (inv as any).total_eur != null && inv.total) {
        b.rateToBase = (inv as any).total_eur / inv.total;
      }
      // Summary totals are NET (без НДС) too — mirror the balance ledger.
      const net = inv.subtotal ?? inv.total;
      if (inv.type === 'payment_confirmation') {
        b.topup += net;
        if (inv.status === 'bonus') b.bonus += net;
      } else if (inv.type === 'topup_request' && inv.status === 'paid') {
        b.topup += net;
      } else if (inv.type === 'spend_invoice') {
        b.spend += net;
      }
    });
    // Base currency converts 1:1 to itself.
    if (buckets[totalsMeta.currency]) buckets[totalsMeta.currency].rateToBase = 1;

    let topup = 0, spend = 0, bonus = 0;
    Object.values(buckets).forEach(b => {
      const rate = b.rateToBase ?? 1;
      topup += b.topup * rate;
      spend += b.spend * rate;
      bonus += b.bonus * rate;
    });
    return { topup, spend, bonus, balance: topup - spend, cashflow: (topup - bonus) - spend };
  }, [filtered, totalsMeta.currency]);


  return (
    <>
      <div className="header">
        <div className="header-title">
          <h1>{t('billing.tabTransactions')}</h1>
          <p className="header-subtitle">{t('billing.txSubtitle')}</p>
        </div>
        {ownAccount && (
          <div className="header-actions">
            {ownAccount.payment_available ? (
              <button
                style={{
                  background: 'var(--accent-gradient)',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  fontSize: 14,
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
                onClick={() => setShowTopup(true)}
              >
                {t('billing.topUp')}
              </button>
            ) : (
              <span
                title={t('billing.noProfileClientTitle')}
                style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}
              >
                {t('billing.topupUnavailableShort')}
              </span>
            )}
          </div>
        )}
      </div>

      {showTopup && ownAccount && (
        <TopUpDialog
          advertiserId={ownAccount.advertiser_id}
          currency={ownAccount.currency}
          cardAvailable={!!ownAccount.card_available}
          bankAvailable={!!ownAccount.bank_available}
          onClose={() => { setShowTopup(false); loadTransactions(); }}
        />
      )}

      <div className="reports-layout">
        <aside className="filters-panel">
          <div className="filter-section">
            <h3 className="section-heading">{t('common.search')}</h3>
            <div className="campaign-search-input-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="campaign-search-input"
                placeholder={t('billing.searchNumberClient')}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="filter-section">
            <h3 className="section-heading">{t('billing.type')}</h3>
            <select
              className="form-select"
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
            >
              {TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
              ))}
            </select>
          </div>

          <div className="filter-section">
            <h3 className="section-heading">{t('billing.colStatus')}</h3>
            <select
              className="form-select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
              ))}
            </select>
          </div>

          <div className="filter-section">
            <h3 className="section-heading">{t('billing.colPeriod')}</h3>
            <div style={{ display: 'flex', gap: '6px' }}>
              <select
                className="form-select"
                style={{ flex: 3, minWidth: 0 }}
                value={periodMonth}
                onChange={e => setPeriodMonth(e.target.value)}
              >
                <option value="">{t('billing.month')}</option>
                {/* Month names come from Intl, so a new interface language needs
                    no extra catalogue entries. The value stays the month number. */}
                {Array.from({ length: 12 }, (_, i) => {
                  const name = new Date(2000, i, 1).toLocaleDateString(currentLocale(), { month: 'long' });
                  return (
                    <option key={i} value={String(i + 1).padStart(2, '0')}>
                      {name.charAt(0).toUpperCase() + name.slice(1)}
                    </option>
                  );
                })}
              </select>
              <select
                className="form-select"
                style={{ flex: 2, minWidth: 0 }}
                value={periodYear}
                onChange={e => setPeriodYear(e.target.value)}
              >
                <option value="">{t('billing.year')}</option>
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {isAdmin && (
            <div className="filter-section">
              <h3 className="section-heading">{t('clients.regionalOffice')}</h3>
              <select
                className="form-select"
                value={officeFilter}
                onChange={e => setOfficeFilter(e.target.value)}
              >
                <option value="">{t('clients.allOffices')}</option>
                {officeOptions.map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          )}

          {isAdmin && (
            <div className="filter-section">
              <h3 className="section-heading">{t('billing.owner')}</h3>
              <div className="campaign-search-container" ref={ownerRef}>
                <div className="campaign-search-input-wrapper">
                  <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  <input
                    type="text"
                    className="campaign-search-input"
                    placeholder={t('billing.phOwner')}
                    value={selectedOwner ? selectedOwner.name : ownerQuery}
                    onChange={e => {
                      setOwnerQuery(e.target.value);
                      setSelectedOwner(null);
                      setShowOwnerDropdown(true);
                    }}
                    onFocus={() => setShowOwnerDropdown(true)}
                  />
                  {selectedOwner && (
                    <button
                      type="button"
                      className="search-clear-btn"
                      onClick={() => { setSelectedOwner(null); setOwnerQuery(''); }}
                      title={t('billing.clear')}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12"></path>
                      </svg>
                    </button>
                  )}
                </div>
                {showOwnerDropdown && !selectedOwner && filteredOwners.length > 0 && (
                  <div className="campaign-search-dropdown">
                    {filteredOwners.map(o => (
                      <div
                        key={`${o.type}:${o.name}`}
                        className="campaign-search-item"
                        onClick={() => {
                          setSelectedOwner(o);
                          setOwnerQuery('');
                          setShowOwnerDropdown(false);
                        }}
                      >
                        <div className="campaign-search-item-name">{o.name}</div>
                        <div className="campaign-search-item-advertiser">
                          {t(o.type === 'Agency' ? 'billing.ownerAgency' : 'billing.ownerFreelancer')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {hasFilters && (
            <div className="filter-section" style={{ marginTop: '8px' }}>
              <button className="btn-secondary" style={{ width: '100%' }} onClick={clearFilters}>
                {t('billing.clearFilters')}
              </button>
            </div>
          )}
        </aside>

        <div className="results-area visible">
          {/* Summary cards */}
          {!loading && filtered.length > 0 && (() => {
            const neutral = '#2c3e50';
            const cards: { label: string; value: number; color: string; sign: string }[] = [
              { label: t('billing.typeTopup'), value: totals.topup, color: neutral, sign: '' },
              { label: t('billing.typeSpend'), value: totals.spend, color: neutral, sign: '' },
              { label: t('billing.colBalance'), value: totals.balance, color: totals.balance >= 0 ? '#27ae60' : '#e53935', sign: totals.balance >= 0 ? '+' : '\u2212' },
            ];
            if (totalsMeta.is_sense_staff) {
              cards.push({ label: t('billing.cardCashflow'), value: totals.cashflow, color: totals.cashflow >= 0 ? '#27ae60' : '#e53935', sign: totals.cashflow >= 0 ? '+' : '\u2212' });
            }
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
                {cards.map(card => {
                  const abs = Math.abs(card.value);
                  const { whole, cents } = formatAmount(abs);
                  return (
                    <div key={card.label} style={{
                      flex: '1 1 130px', background: '#fff', borderRadius: '10px', padding: '16px 20px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: 500, color: '#90a4ae', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                        {card.label}
                      </div>
                      <div style={{ fontSize: '22px', fontWeight: 700, color: card.color }}>
                        {card.sign}{whole}<span style={{ fontSize: '14px', fontWeight: 500 }}>.{cents}</span>
                        <span style={{ fontSize: '13px', color: '#999', fontWeight: 500, marginLeft: '4px' }}>{totalsMeta.currency}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
          {loading ? (
            <div className="users-loading">
              <div className="users-spinner" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="editor-empty">
              <div className="editor-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6z" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2v6h6" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 13H8" strokeLinecap="round"/>
                  <path d="M16 17H8" strokeLinecap="round"/>
                  <path d="M10 9H8" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>{t('billing.noTransactionsFound')}</h3>
              <p>{t(hasFilters ? 'billing.tryAdjusting' : 'billing.txAppearHere')}</p>
            </div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th style={{ width: '140px' }}>{t('billing.colNumber')}</th>
                    <th>{t('clients.colClient')}</th>
                    <th style={{ width: '120px' }}>{t('billing.colPeriod')}</th>
                    <th style={{ width: '90px' }}>{t('billing.colStatus')}</th>
                    <th style={{ width: '130px', textAlign: 'right' }}>{t('billing.typeTopup')}</th>
                    <th style={{ width: '130px', textAlign: 'right' }}>{t('billing.typeSpend')}</th>
                    <th style={{ width: '100px' }}>{t('billing.colDate')}</th>
                    <th style={{ width: '100px' }}>{t('billing.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(inv => {
                    const isPureTopup = inv.type === 'payment_confirmation' || inv.type === 'topup_request';
                    const showTopup = isPureTopup;
                    const showSpend = inv.type === 'spend_invoice';
                    const isPending = inv.type === 'topup_request' && inv.status !== 'paid';
                    // TOP-UP / SPEND are shown NET (без НДС) — they mirror balance
                    // movements. VAT lives only inside the invoice document (PDF).
                    const { whole, cents } = formatAmount(inv.subtotal ?? inv.total);
                    return (
                      <tr key={inv.id}>
                        <td>
                          <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 500 }}>{inv.invoice_number}</span>
                        </td>
                        <td>
                          <span className="users-user-name" style={{ fontSize: '13px' }}>
                            {inv.scope === 'agency_account'
                              ? (inv as any).agency_name || t('billing.agencyNum', { id: inv.agency_id })
                              : inv.advertiser_name || inv.advertiser_id}
                          </span>
                          <span className="users-user-email" style={{ display: 'block' }}>
                            {(inv as any).client_id}{inv.office_name ? ` · ${inv.office_name}` : ''}
                            {(inv as any).agency_name && inv.scope !== 'agency_account' && (
                              <span className="clients-agency-badge">{(inv as any).agency_name}</span>
                            )}
                            {(inv as any).freelancer_name && inv.scope !== 'agency_account' && (
                              <span className="clients-agency-badge" style={{ background: '#e8f5e9', color: '#2e7d32' }}>{(inv as any).freelancer_name}</span>
                            )}
                            {(inv as any).is_self_service && (
                              <span className="clients-agency-badge" style={{ background: '#f0f0f0', color: '#666' }}>{t('clients.selfService')}</span>
                            )}
                          </span>
                        </td>
                        <td style={{ fontSize: '13px', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                          {inv.period_start
                            ? new Date(inv.period_start).toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' })
                            : <span style={{ color: '#ccc' }}>{'\u2014'}</span>}
                        </td>
                        <td>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 500,
                            background: getStatusColor(inv.status),
                            color: '#fff',
                          }}>
                            {getStatusLabel(inv.status)}
                          </span>
                        </td>
                        {/* Top-Up column */}
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {showTopup ? (
                            <>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: isPending ? '#999' : '#27ae60' }}>+{whole}</span>
                              <span style={{ fontSize: '11px', color: isPending ? '#999' : '#27ae60' }}>.{cents}</span>
                              {' '}
                              <span style={{ fontSize: '11px', color: '#999', fontWeight: 500 }}>{inv.currency}</span>
                              {isPending && (
                                <span style={{ display: 'block', fontSize: '10px', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                                  {t('billing.awaitingPayment')}
                                </span>
                              )}
                            </>
                          ) : (
                            <span style={{ color: '#ccc' }}>{'\u2014'}</span>
                          )}
                        </td>
                        {/* Spend column */}
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {showSpend ? (
                            <>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#e53935' }}>{'\u2212'}{whole}</span>
                              <span style={{ fontSize: '11px', color: '#e53935' }}>.{cents}</span>
                              {' '}
                              <span style={{ fontSize: '11px', color: '#999', fontWeight: 500 }}>{inv.currency}</span>
                            </>
                          ) : (
                            <span style={{ color: '#ccc' }}>{'\u2014'}</span>
                          )}
                        </td>
                        <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                          {inv.created_at ? formatDate(inv.created_at) : ''}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            {inv.status !== 'ongoing' && inv.invoice_number && (
                              <button
                                className="btn-secondary"
                                onClick={() => handleDownloadPdf(inv.id, inv.invoice_number, inv.type)}
                                title={t('billing.downloadPdf')}
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                              >
                                PDF
                              </button>
                            )}
                            {isPureTopup && isAdmin && (
                              <button
                                className="btn-secondary"
                                onClick={() => handleDelete(inv.id, inv.invoice_number, inv.total, inv.type, inv.status)}
                                title={t('billing.deleteTransaction')}
                                style={{ padding: '4px 8px', fontSize: '12px', color: '#e53935' }}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                                  <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            )}
                            {/* A proforma waiting for a bank payment needs closing
                                too — the backend has handled that since it was
                                written, but only the spend invoice ever offered
                                the button, so ops closed proformas by making a
                                manual top-up and the document stayed open. */}
                            {(inv.type === 'spend_invoice' || inv.type === 'topup_request')
                              && inv.status === 'issued' && isAdmin && (
                              <button
                                className="btn-secondary"
                                onClick={() => handleStatusChange(inv.id, 'paid')}
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                              >
                                {t('billing.markPaid')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {confirmDelete && (
        <div onClick={() => setConfirmDelete(null)}
             style={{ position: 'fixed', inset: 0, background: 'rgba(20,16,40,0.45)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: 14, padding: 22, width: 420, maxWidth: '92%',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700 }}>
              {t('billing.confirmDeleteTxTitle', { label: confirmDelete.label })}
            </h3>
            <p style={{ margin: '0 0 18px', fontSize: 13, color: '#777' }}>
              {t(confirmDelete.reverses ? 'billing.deleteTailReverses' : 'billing.deleteTailPending')}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="users-btn users-btn-secondary" onClick={() => setConfirmDelete(null)}>{t('common.cancel')}</button>
              <button className="users-btn" style={{ background: '#c0392b', color: '#fff' }} onClick={doDelete}>{t('common.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TransactionsPage;
